import React from "react";
import { Checkbox, Stack, Text, Group } from "@mantine/core";
import { SalonService } from "../../types/deposit";
import { groupServicesByType } from "../../utils/depositUtils";

interface ServiceSelectionProps {
  services: SalonService[];
  selectedServiceIds: string[];
  onSelectionChange: (serviceIds: string[]) => void;
  error?: string;
}

const ServiceSelection = ({
  services,
  selectedServiceIds,
  onSelectionChange,
  error,
}: ServiceSelectionProps) => {
  const groupedServices = groupServicesByType(services);

  const handleServiceToggle = (serviceId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedServiceIds, serviceId]);
    } else {
      onSelectionChange(selectedServiceIds.filter((id) => id !== serviceId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(services.map((service) => service.id));
    } else {
      onSelectionChange([]);
    }
  };

  const allSelected =
    services.length > 0 && selectedServiceIds.length === services.length;
  const someSelected =
    selectedServiceIds.length > 0 &&
    selectedServiceIds.length < services.length;

  return (
    <Stack gap="sm">
      <Text size="sm" fw={500}>
        Applies to Services
      </Text>

      {services.length > 0 && (
        <Checkbox
          label="Select All Services"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(event) => handleSelectAll(event.currentTarget.checked)}
        />
      )}

      {Object.entries(groupedServices).map(([type, typeServices]) => (
        <Stack key={type} gap="xs">
          <Text size="sm" fw={500} c="dimmed">
            {type}
          </Text>
          {typeServices.map((service) => (
            <Checkbox
              key={service.id}
              label={service.name}
              checked={selectedServiceIds.includes(service.id)}
              onChange={(event) =>
                handleServiceToggle(service.id, event.currentTarget.checked)
              }
            />
          ))}
        </Stack>
      ))}

      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};

export default ServiceSelection;
